import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { FORMA_LINK_BTN } from '../../utils/listPageUi';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell
} from 'recharts';
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  BanknotesIcon,
  PresentationChartBarIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  BuildingLibraryIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
// PDF Export libraries
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { REPORTS_SOURCE_KEY } from '../../utils/queryKeys';
import { invalidateFormationGraph } from '../../utils/invalidateFormationGraph';
import {
  fetchReportsSourceData,
  computeFinancialReport,
  computeStudentsReport,
  computeCoursesReport,
  computeSalesReport,
  computeReportsOverview,
} from '../../utils/reportsData';

const ReportsPage = () => {
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();
  const [dateRange, setDateRange] = useState('month');
  const [reportType, setReportType] = useState('financial');
  const [isExporting, setIsExporting] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'table'

  // Reset view mode to chart when report type changes
  useEffect(() => {
    setViewMode('chart');
  }, [reportType]);

  // Refetch when date filters change (same shared cache, recompute in useMemo)
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: REPORTS_SOURCE_KEY });
  }, [dateRange, customStartDate, customEndDate, queryClient]);
  
  // Set default date range based on current date
  useEffect(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setCustomStartDate(firstDayOfMonth.toISOString().split('T')[0]);
    setCustomEndDate(lastDayOfMonth.toISOString().split('T')[0]);
  }, []);
  
  const {
    data: sourceData,
    isLoading,
    refetch: refetchSource,
  } = useQuery(REPORTS_SOURCE_KEY, fetchReportsSourceData, {
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  const organizationSettings = sourceData?.settings || {
    gymName: 'FORMAPRO',
    address: 'École de Formation',
    phone: 'Téléphone non renseigné',
    email: 'contact@formapro.com',
    logo: null,
  };

  const financialData = React.useMemo(
    () => (sourceData ? computeFinancialReport(sourceData, dateRange, customStartDate, customEndDate) : null),
    [sourceData, dateRange, customStartDate, customEndDate]
  );

  const studentsData = React.useMemo(
    () => (sourceData ? computeStudentsReport(sourceData) : null),
    [sourceData]
  );

  const coursesData = React.useMemo(
    () => (sourceData ? computeCoursesReport(sourceData) : null),
    [sourceData]
  );

  const salesData = React.useMemo(
    () => (sourceData ? computeSalesReport(sourceData, dateRange, customStartDate, customEndDate) : null),
    [sourceData, dateRange, customStartDate, customEndDate]
  );

  const dashboardData = React.useMemo(
    () => (sourceData ? computeReportsOverview(sourceData) : null),
    [sourceData]
  );

  // Format pour la monnaie (DA)
  const formatCurrency = (value) => {
    return `${value.toLocaleString('fr-FR')} DA`;
  };

  // Render financial table view
  const renderFinancialTable = (data) => {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center text-slate-600 dark:text-slate-400">
            <p className="text-lg mb-2">📊 {t('reportsPage.noRevenueData')}</p>
            <p className="text-sm mb-2" dangerouslySetInnerHTML={{ __html: t('reportsPage.goToPayments').replace('Paiements', '<strong>Paiements</strong>') }}></p>
            <p className="text-xs">{t('reportsPage.dataWillAppear')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto h-96">
        <table className="min-w-full">
          <thead>
            <tr className="bg-slate-100/80 dark:bg-slate-800/40">
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {t('reportsPage.periodCol')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {t('reportsPage.revenueCol')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {t('reportsPage.expensesCol')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {t('reportsPage.profitCol')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {t('dashboard.statBenefit') || 'Marge'}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {data.map((item, index) => {
              const profit = item.revenue - item.expenses;
              const margin = item.revenue > 0 ? ((profit / item.revenue) * 100) : 0;
              
              return (
                <tr key={index} className="hover:bg-slate-100/80 dark:bg-slate-800/40 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-50">
                    {item.month}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className="text-forma-700 dark:text-forma-300 font-medium">
                      {formatCurrency(item.revenue)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className="text-red-400 font-medium">
                      {formatCurrency(item.expenses)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className={`font-medium ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(profit)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className={`font-medium ${margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {margin.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Map data for charts with debugging
  const financialChartData = React.useMemo(() => {
    const timeSeries = financialData?.report?.data?.timeSeries;
    console.log('📊 Chart Data Debug:', {
      hasFinancialData: !!financialData,
      hasReport: !!financialData?.report,
      hasData: !!financialData?.report?.data,
      hasTimeSeries: !!timeSeries,
      timeSeriesLength: timeSeries?.length || 0,
      timeSeries: timeSeries
    });
    
    if (!timeSeries || timeSeries.length === 0) {
      console.log('📊 No timeSeries data generated - returning empty chart');
      const currentDate = new Date();
      const emptyData = [];
      
      // Create empty months for structure
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const monthStr = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
        
        emptyData.push({
          month: monthStr,
          revenue: 0,
          expenses: 0,
          profit: 0
        });
      }
      
      return emptyData;
    }
    
    // Check if we have ANY revenue at all
    const hasRevenue = timeSeries.some(item => item.income > 0);
    console.log('📊 Chart Data Analysis:', {
      timeSeriesLength: timeSeries.length,
      hasRevenue,
      totalRevenue: timeSeries.reduce((sum, item) => sum + item.income, 0),
      sampleMonth: timeSeries[timeSeries.length - 1] // Current month
    });
    
    if (!hasRevenue) {
      console.log('📊 No revenue found in timeSeries - but keeping structure for potential future data');
    }
    
    return timeSeries.map(item => ({
    month: item.period,
      revenue: item.income || 0,
      expenses: item.expenses || 0,
      profit: item.profit || 0
    }));
  }, [financialData]);

  const studentsChartData = studentsData?.monthlyData || [];
  const coursesPieData = coursesData?.distribution || [];
  const salesBarData = salesData?.monthlySales || [];

  // Traitement des données en fonction du type de rapport
  let reportData = [];
  let reportTitle = '';
  let reportDescription = '';
  let chartComponent = null;

  switch (reportType) {
    case 'financial':
      reportData = financialChartData;
      reportTitle = t('reportsPage.financialReportTitle');
      reportDescription = t('reportsPage.financialReportDesc');
      
      console.log('📊 Financial Chart Rendering:', {
        reportDataLength: reportData?.length || 0,
        reportData: reportData,
        sampleItem: reportData?.[0]
      });
      
      chartComponent = (
        <div className="h-96">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              <span className="ml-3 text-slate-900 dark:text-slate-50">Chargement des données financières...</span>
            </div>
          ) : viewMode === 'table' ? (
            renderFinancialTable(reportData)
          ) : reportData && reportData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={reportData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" tickFormatter={formatCurrency} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                itemStyle={{ color: '#f8fafc' }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Legend wrapperStyle={{ color: '#94a3b8' }} />
              <Bar dataKey="revenue" name="Revenus" fill="#3b82f6" />
              <Bar dataKey="expenses" name="Dépenses" fill="#ef4444" />
              <Bar dataKey="profit" name="Bénéfices" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-600 dark:text-slate-400">
                <p className="text-lg mb-2">📊 {t('reportsPage.noRevenueData')}</p>
                <p className="text-sm mb-2" dangerouslySetInnerHTML={{ __html: t('reportsPage.goToPayments').replace('Paiements', '<strong>Paiements</strong>') }}></p>
                <p className="text-xs">{t('reportsPage.dataWillAppear')}</p>
              </div>
            </div>
          )}
        </div>
      );
      break;

    case 'students':
      reportData = studentsChartData;
      reportTitle = 'Rapport des Étudiants';
      reportDescription = 'Statistiques sur l\'évolution du nombre d\'étudiants';
      chartComponent = (
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={reportData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                itemStyle={{ color: '#f8fafc' }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Legend wrapperStyle={{ color: '#94a3b8' }} />
              <Line type="monotone" dataKey="nouveaux" name="Nouveaux" stroke="#3b82f6" activeDot={{ r: 8 }} />
              <Line type="monotone" dataKey="actifs" name="Actifs" stroke="#10b981" />
              <Line type="monotone" dataKey="inactifs" name="Inactifs" stroke="#ef4444" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
      break;

    case 'courses':
      reportData = coursesPieData;
      reportTitle = 'Répartition des Cours';
      reportDescription = 'Distribution des étudiants par cours';
      
      console.log('📊 Courses Pie Chart Data:', reportData);
      
      chartComponent = (
        <div className="h-96">
          {reportData && reportData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={reportData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {reportData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} étudiant${value > 1 ? 's' : ''}`,
                    props.payload.revenue ? `Revenus: ${props.payload.revenue.toLocaleString()} DA` : ''
                  ]}
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  itemStyle={{ color: '#f8fafc' }}
                  labelStyle={{ color: '#94a3b8' }}
                />
                <Legend wrapperStyle={{ color: '#94a3b8' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-600 dark:text-slate-400">
                <p className="text-lg mb-2">📚 Aucun cours disponible</p>
                <p className="text-sm">Créez des cours dans la section Cours pour voir la répartition</p>
              </div>
            </div>
          )}
        </div>
      );
      break;

    case 'sales':
      reportData = salesBarData;
      reportTitle = 'Ventes Mensuelles';
      reportDescription = 'Évolution des ventes par mois';
      chartComponent = (
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={reportData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" tickFormatter={(value) => `${value.toLocaleString()} DA`} />
              <Tooltip 
                formatter={(value) => `${value.toLocaleString()} DA`}
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                itemStyle={{ color: '#f8fafc' }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Legend wrapperStyle={{ color: '#94a3b8' }} />
              <Bar dataKey="sales" name="Ventes" fill="#3b82f6" />
              <Bar dataKey="transactions" name="Transactions" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
      break;
    default:
      reportData = financialChartData;
  }

  // Options de période
  const dateRangeOptions = [
    { id: 'week', name: 'Cette semaine' },
    { id: 'month', name: 'Ce mois' },
    { id: 'quarter', name: 'Ce trimestre' },
    { id: 'year', name: 'Cette année' },
    { id: 'custom', name: 'Personnalisé' },
  ];

  // Types de rapports
  const reportTypes = [
    { id: 'financial', name: 'Finances', icon: BanknotesIcon },
    { id: 'students', name: 'Étudiants', icon: AcademicCapIcon },
    { id: 'courses', name: 'Cours', icon: ClipboardDocumentCheckIcon },
    { id: 'sales', name: 'Sales', icon: PresentationChartBarIcon },
  ];

  const handleExportReport = async () => {
    setIsExporting(true);
    console.log(`📄 Exporting real-time ${reportType} report for period ${dateRange}...`);
    
    // Debug: Check data availability before export
    console.log('📊 Export Debug - Data availability:', {
      reportType,
      organizationSettings: organizationSettings ? '✅ Available' : '❌ Missing',
      financialData: financialData ? '✅ Available' : '❌ Missing',
      studentsData: studentsData ? '✅ Available' : '❌ Missing',
      coursesData: coursesData ? '✅ Available' : '❌ Missing',
      salesData: salesData ? '✅ Available' : '❌ Missing',
      isLoading,
    });
    
    // Check if data is still loading
    if (isLoading) {
      toast.error(`⏳ ${t('reportsPage.waitForData')}`);
      setIsExporting(false);
      return;
    }
    
    try {
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const margin = 20;
      let yPosition = margin;

      // Helper function to get period display text
      const getPeriodDisplayText = () => {
        const options = {
          week: 'Cette semaine',
          month: 'Ce mois',
          quarter: 'Ce trimestre', 
          year: 'Cette année',
          custom: `${customStartDate} au ${customEndDate}`
        };
        return options[dateRange] || t('reportsPage.periodDisplay');
      };

      // Add professional header with school branding
      const addHeader = () => {
        // Add school logo if available
        if (organizationSettings?.logo) {
          try {
            pdf.addImage(organizationSettings.logo, 'PNG', margin, yPosition, 30, 30);
          } catch (error) {
            console.warn('Could not add logo to PDF:', error);
          }
        }

        // School name and title
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        pdf.text(organizationSettings?.gymName || 'FORMAPRO', organizationSettings?.logo ? margin + 40 : margin, yPosition + 10);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'normal');
        pdf.text(organizationSettings?.address || 'École de Formation', organizationSettings?.logo ? margin + 40 : margin, yPosition + 18);
        
        if (organizationSettings?.phone) {
          pdf.text(`Tél: ${organizationSettings.phone}`, organizationSettings?.logo ? margin + 40 : margin, yPosition + 25);
        }
        
        // Report title and info on the right
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        const reportTitle = `Rapport ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`;
        pdf.text(reportTitle, pageWidth - margin, yPosition + 10, { align: 'right' });
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        const periodText = getPeriodDisplayText();
        const periodLabel = language === 'ar' ? 'الفترة:' : 'Période:';
        pdf.text(`${periodLabel} ${periodText}`, pageWidth - margin, yPosition + 18, { align: 'right' });

        const currentDate = new Date().toLocaleDateString(language === 'ar' ? 'ar-DZ' : 'fr-FR');
        const generatedLabel = language === 'ar' ? 'تم إنشاؤه:' : 'Généré le:';
        pdf.text(`${generatedLabel} ${currentDate}`, pageWidth - margin, yPosition + 25, { align: 'right' });
        
        // Add horizontal line
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPosition + 35, pageWidth - margin, yPosition + 35);
        
        return yPosition + 45;
      };

      yPosition = addHeader();

      // Add report content based on type
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      
      switch (reportType) {
        case 'financial':
          console.log('📄 Adding financial report section...');
          addFinancialReport(pdf, yPosition, margin);
          break;
        case 'students':
          console.log('📄 Adding students report section...');
          addStudentsReport(pdf, yPosition, margin);
          break;
        case 'courses':
          console.log('📄 Adding courses report section...');
          addCoursesReport(pdf, yPosition, margin);
          break;
        case 'sales':
          console.log('📄 Adding sales report section...');
          addSalesReport(pdf, yPosition, margin);
          break;
      }

      // Add footer
      const addFooter = () => {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(
          `Document généré automatiquement par ${organizationSettings?.gymName || 'FORMAPRO'} - ${new Date().toLocaleString('fr-FR')}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      };

      addFooter();

      // Save PDF
      const fileName = `rapport-${reportType}-${dateRange}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success(`✅ Rapport PDF exporté avec succès: ${fileName}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('❌ Erreur lors de l\'exportation du rapport PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // Helper functions for different report types
  const addFinancialReport = (pdf, startY, margin) => {
    console.log('📊 Financial Report Data Check:', {
      financialData: !!financialData,
      reportData: !!financialData?.report,
      dataSection: !!financialData?.report?.data,
      summary: !!financialData?.report?.data?.summary,
      timeSeries: financialData?.report?.data?.timeSeries?.length || 0
    });
    
    let yPos = startY;
    const summary = financialData?.report?.data?.summary;
    const timeSeries = financialData?.report?.data?.timeSeries || [];

    // Summary section
    pdf.text('Résumé Financier', margin, yPos);
    yPos += 10;

    if (summary) {
      const summaryData = [
        ['Revenus totaux', `${(summary.totalIncome || 0).toLocaleString()} DA`],
        ['Dépenses totales', `${(summary.totalExpenses || 0).toLocaleString()} DA`],
        ['Bénéfice net', `${(summary.netProfit || 0).toLocaleString()} DA`],
        ['Marge bénéficiaire', `${(summary.profitMargin || 0).toFixed(1)}%`]
      ];

      autoTable(pdf, {
        startY: yPos,
        head: [['Indicateur', 'Valeur']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [68, 84, 195] },
        margin: { left: margin, right: margin }
      });

      yPos = pdf.lastAutoTable?.finalY ? pdf.lastAutoTable.finalY + 15 : yPos + 50;
    } else {
      pdf.text('Aucune donnée financière disponible', margin, yPos);
      yPos += 15;
    }

    // Time series data
    if (timeSeries.length > 0) {
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Évolution Temporelle', margin, yPos);
      yPos += 10;

      const timeSeriesData = timeSeries.map(item => [
        item.period || 'N/A',
        `${(item.income || 0).toLocaleString()} DA`,
        `${(item.expenses || 0).toLocaleString()} DA`,
        `${(item.profit || 0).toLocaleString()} DA`
      ]);

      autoTable(pdf, {
        startY: yPos,
        head: [['Période', 'Revenus', 'Dépenses', 'Bénéfice']],
        body: timeSeriesData,
        theme: 'striped',
        headStyles: { fillColor: [34, 197, 94] },
        margin: { left: margin, right: margin }
      });
    }
  };

  const addStudentsReport = (pdf, startY, margin) => {
    console.log('👥 Students Report Data Check:', {
      studentsData: !!studentsData,
      summary: !!studentsData?.summary
    });
    
    let yPos = startY;
    const summary = studentsData?.summary;

    pdf.text('Rapport Étudiants', margin, yPos);
    yPos += 10;

    if (summary) {
      const studentsTableData = [
        ['Nouveaux étudiants', (summary.newThisMonth || 0).toString()],
        ['Étudiants actifs', (summary.active || 0).toString()],
        ['Étudiants inactifs', (summary.inactive || 0).toString()],
        ['Total étudiants', (summary.total || 0).toString()]
      ];

      autoTable(pdf, {
        startY: yPos,
        head: [['Indicateur', 'Nombre']],
        body: studentsTableData,
        theme: 'grid',
        headStyles: { fillColor: [147, 51, 234] },
        margin: { left: margin, right: margin }
      });
    } else {
      pdf.text('Aucune donnée étudiante disponible', margin, yPos);
    }
  };

  const addCoursesReport = (pdf, startY, margin) => {
    console.log('📚 Courses Report Data Check:', {
      coursesData: !!coursesData,
      courseAnalytics: !!coursesData?.courseAnalytics,
      analyticsCount: Object.keys(coursesData?.courseAnalytics || {}).length
    });
    
    let yPos = startY;
    const courseAnalytics = coursesData?.analytics?.courseDetails || [];

    pdf.text('Rapport Cours', margin, yPos);
    yPos += 10;

    const coursesTableData = courseAnalytics.map((course) => [
      course.name || 'N/A',
      (course.enrolledStudents || 0).toString(),
      `${(course.revenue || 0).toLocaleString()} DA`,
      `${(course.completionRate || 0).toFixed(1)}%`
    ]);

    if (coursesTableData.length > 0) {
      autoTable(pdf, {
        startY: yPos,
        head: [['Cours', 'Étudiants', 'Revenus', 'Taux de Réussite']],
        body: coursesTableData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
        margin: { left: margin, right: margin }
      });
    } else {
      pdf.text('Aucune donnée de cours disponible', margin, yPos);
    }
  };

  const addSalesReport = (pdf, startY, margin) => {
    console.log('💰 Sales Report Data Check:', {
      salesData: !!salesData,
      salesByMethod: !!salesData?.salesByMethod,
      methodsCount: Object.keys(salesData?.salesByMethod || {}).length
    });
    
    let yPos = startY;
    const salesByMethod = salesData?.salesByMethod || [];

    pdf.text('Rapport Ventes', margin, yPos);
    yPos += 10;

    const salesTableData = salesByMethod.map((row) => [
      row.name || 'N/A',
      `${(row.value || 0).toLocaleString()} DA`,
    ]);

    if (salesTableData.length > 0) {
      autoTable(pdf, {
        startY: yPos,
        head: [['Méthode de Paiement', 'Montant']],
        body: salesTableData,
        theme: 'grid',
        headStyles: { fillColor: [245, 101, 101] },
        margin: { left: margin, right: margin }
      });
    } else {
      pdf.text('Aucune donnée de ventes disponible', margin, yPos);
    }
  };

  const handleRefresh = async () => {
    setIsExporting(true);
    try {
      await invalidateFormationGraph(queryClient);
      await refetchSource();
      toast.success('Données actualisées avec succès!');
    } catch (error) {
      console.error('Error refreshing reports:', error);
      toast.error('Erreur lors de l\'actualisation des données');
    } finally {
      setIsExporting(false);
    }
  };

  const formaLinkBtn = FORMA_LINK_BTN;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <PageHeader
        title={t('reportsPageTitle')}
        description={t('pageDescriptions.reports')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isExporting}
              className={clsx(
                'rounded-xl border border-slate-200 bg-white p-2 shadow-sm transition dark:border-slate-600 dark:bg-slate-900',
                isExporting
                  ? 'cursor-not-allowed text-slate-400 opacity-60'
                  : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
              )}
              title={t('commonActions.refreshData')}
            >
              <ArrowPathIcon className={clsx('h-5 w-5', isExporting && 'animate-spin')} />
            </button>
            <button
              type="button"
              onClick={handleExportReport}
              disabled={isExporting}
              className={clsx(formaLinkBtn, 'disabled:cursor-not-allowed disabled:opacity-50')}
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>{t('commonActions.exportPdf') || 'Exporter'}</span>
            </button>
          </div>
        }
      />

      {/* Cartes statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm">{t('dashboard.statRevenue') || 'Revenus'}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mt-2">
                {dashboardData?.data?.monthlyRevenue ? 
                  `${dashboardData.data.monthlyRevenue.toLocaleString()} DA` : 
                  '0 DA'
                }
              </p>
            </div>
            <div className="p-3 rounded-lg bg-forma-500/15">
              <BanknotesIcon className="h-6 w-6 text-forma-700 dark:text-forma-300" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-green-400 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
            </svg>
            <span>{t('schedulePageLoadedEvents', { n: 0 }).replace(/^.*?\d+\s*/, '') || 'Temps réel'}</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm">{t('reportsPage.activeStudentsLabel')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mt-2">
                {dashboardData?.data?.activeStudents || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-teal-500/15">
              <AcademicCapIcon className="h-6 w-6 text-teal-700 dark:text-teal-300" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-green-400 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
            </svg>
            <span>{t('reportsPage.activeStudentsOnly')}</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm">{t('dashboard.statCourses') || 'Cours'}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mt-2">
                {dashboardData?.data?.activeCourses || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-500/20">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-yellow-400 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l1 1a1 1 0 01-1.414 1.414L10 5.414 8.707 6.707a1 1 0 01-1.414-1.414l1-1A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            <span>{t('coursesPage.statsOpenTitle') || 'Cours disponibles'}</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Taux d'occupation</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mt-2">
                {dashboardData?.data?.totalStudents > 0 ? 
                  `${Math.round((dashboardData.data.activeStudents / dashboardData.data.totalStudents) * 100)}%` : 
                  '0%'
                }
              </p>
            </div>
            <div className="p-3 rounded-lg bg-orange-500/20">
              <BuildingLibraryIcon className="h-6 w-6 text-orange-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-green-400 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
            </svg>
            <span>Taux d'occupation calculé</span>
          </div>
        </motion.div>
      </div>

      {/* Filtres et sélection de rapport */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-1">
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 backdrop-blur-xl border border-slate-200 dark:border-slate-600 h-full">
            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">{t('reportsPage.reportTypeLabel')}</h2>
            <div className="space-y-2">
              {reportTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setReportType(type.id)}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                    reportType === type.id
                      ? 'border border-forma-200/80 bg-forma-50/80 dark:border-forma-900/50 dark:bg-forma-950/30'
                      : 'hover:bg-slate-100/80 dark:bg-slate-800/40'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${reportType === type.id ? 'bg-forma-500/15' : 'bg-slate-100/80 dark:bg-slate-800/40'}`}>
                    <type.icon className={`h-5 w-5 ${reportType === type.id ? 'text-forma-700 dark:text-forma-300' : 'text-slate-600 dark:text-slate-400'}`} />
                  </div>
                  <span className={`text-sm font-medium ${reportType === type.id ? 'text-slate-900 dark:text-slate-50' : 'text-slate-600 dark:text-slate-400'}`}>
                    {type.name}
                  </span>
                </button>
              ))}
            </div>

            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50 mt-6 mb-4">{t('reportsPage.periodLabel')}</h2>
            <div className="space-y-2">
              {dateRangeOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setDateRange(option.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg text-sm transition-colors ${
                    dateRange === option.id
                      ? 'bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:bg-slate-800/40 hover:text-slate-900 dark:text-slate-50'
                  }`}
                >
                  <span>{option.name}</span>
                  {dateRange === option.id && (
                    <div className="h-2 w-2 rounded-full bg-blue-400"></div>
                  )}
                </button>
              ))}
            </div>

            {dateRange === 'custom' && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">{t('reportsPage.startDateLabel')}</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2 text-slate-900 dark:text-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-forma-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">{t('reportsPage.endDateLabel')}</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2 text-slate-900 dark:text-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-forma-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 backdrop-blur-xl border border-slate-200 dark:border-slate-600">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">{reportTitle}</h2>
                <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">{reportDescription}</p>
              </div>
              {reportType === 'financial' && (
                <div className="mt-4 sm:mt-0 flex items-center">
                  <div className="flex items-center bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-1">
                    <button 
                      onClick={() => setViewMode('chart')}
                      className={`px-3 py-1 text-sm rounded-md transition-all duration-200 ${
                        viewMode === 'chart' 
                          ? 'bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 font-medium' 
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100/80 dark:bg-slate-800/40'
                      }`}
                    >
                      Graphique
                    </button>
                    <button 
                      onClick={() => setViewMode('table')}
                      className={`px-3 py-1 text-sm rounded-md transition-all duration-200 ${
                        viewMode === 'table' 
                          ? 'bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 font-medium' 
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100/80 dark:bg-slate-800/40'
                      }`}
                    >
                      Tableau
                    </button>
                  </div>
                </div>
              )}
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white/30"></div>
              </div>
            ) : (
              chartComponent
            )}
          </div>
        </div>
      </div>

    </motion.div>
  );
};

export default ReportsPage;